import { Injectable, InjectionToken } from "@angular/core";
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HTTP_INTERCEPTORS,
  HttpRequest,
} from "@angular/common/http";

import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";

import { CsrfService } from "./tokens/csrf.service";
import { HttpErrorHandlerService } from "./error-handlers/http-error-handler.service";
import { TokenService } from "./tokens/token.service";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  // --- Interceptor Constructor ---

  constructor(
    private csrfService: CsrfService,
    private httpErrorHandlerService: HttpErrorHandlerService,
    private tokenService: TokenService
  ) {}

  // --- Interface Methods ---

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    var isUpdate: boolean = request.method !== "GET";

    let authReq: HttpRequest<any> = this.buildRequestOptions(request);

    /*
     * Forward request and catch errors
     */
    return next
      .handle(authReq)
      .pipe(
        catchError((error) => this.handleError(next, authReq, error, isUpdate))
      );
  }

  // --- Private Helpers ---

  private buildRequestOptions(request: HttpRequest<any>): HttpRequest<any> {
    let token = this.tokenService.getToken();
    let csrfToken = this.csrfService.getToken();

    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        "Cache-Control": "no-store, no-cache, must-revalidate",
        Pragma: "no-cache",
        "X-CSRF-Token": csrfToken,
        ...(request.headers.has("Content-Type")
          ? {}
          : { "Content-Type": "application/json" }),
      },
      withCredentials: true,
    });
  }

  private handleError(
    next: HttpHandler,
    authReq: HttpRequest<any>,
    error: any,
    isUpdate: boolean
  ) {
    switch (error.status) {
      case 403:
        if (!isUpdate) {
          this.httpErrorHandlerService.handle403(error);
        }
        break;
      case 401:
        if (this.tokenService.hasToken()) {
          let refreshReq = this.buildRequestOptions(authReq);

          return next.handle(refreshReq).pipe(
            catchError((error) => {
              this.httpErrorHandlerService.handle401(isUpdate);
              return throwError(error);
            })
          );
        } else {
          this.httpErrorHandlerService.handle401(isUpdate);
        }
        break;
      default:
        break;
    }

    return throwError(error);
  }
}

/*
 * Export provider to use AuthInterceptor in place of HTTP_INTERCEPTORS in AppModule
 */

// provides necessary type annotation for the exported AuthInterceptorProvider
const httpInterceptors: InjectionToken<HttpInterceptor[]> = HTTP_INTERCEPTORS;

export const AuthInterceptorProvider = {
  provide: httpInterceptors,
  useClass: AuthInterceptor,
  multi: true,
};
